import { getServiceBaseUrl, API_CONFIG } from '../config/api';

// API configuration for content identification service
// To use this service, set the base URL in your .env file:
// REACT_APP_CONTENT_API_BASE_URL=http://localhost:7860
const CONTENT_API_CONFIG = {
  BASE_URL: process.env.REACT_APP_CONTENT_API_BASE_URL || 'http://110.183.1.67:7860',
  ENDPOINT: '/api/v1/run/26e9e547-17ee-463d-8113-8ea5fb80a6a9',
  CHAT_INPUT_KEY: 'ChatInput-PlcQ4', // The specific input key from your Langflow,
  API_KEY: process.env.REACT_APP_CONTENT_API_KEY || 'sk-ExXIcJ8w-3h8IxGgsMT6MJQkOmktbcJ1iWJKdtgC44s', // Set in .env file
};

// Request payload interface matching your Langflow structure
interface ContentIdentificationRequest {
  output_type: 'chat';
  input_type: 'chat';
  tweaks: {
    [key: string]: {
      input_value: string;
      available_contents?: string;
      candidate_context?: string;
    };
  };
  session_id: string;
}

// Response interfaces based on the API specification
interface ContentIdentificationApiResponse {
  outputs: Array<{
    outputs: Array<{
      results: {
        message: {
          text: string;
        };
      };
    }>;
  }>;
}

// The expected response format from the API
// Now returns plain text content_id directly
export type ContentIdentificationResult = string;

export class ContentIdentificationApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: any
  ) {
    super(message);
    this.name = 'ContentIdentificationApiError';
  }
}

export class ContentIdentificationService {
  /**
   * Get authentication headers with JWT token and API key
   */
  private static getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem(API_CONFIG.AUTH.TOKEN_KEY);
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(CONTENT_API_CONFIG.API_KEY && { 'x-api-key': CONTENT_API_CONFIG.API_KEY }),
    };
  }

  /**
   * Generate a session ID for the user
   */
  private static getSessionId(): string {
    // Use user ID from auth if available, otherwise generate one
    const token = localStorage.getItem(API_CONFIG.AUTH.TOKEN_KEY);
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return `user_${payload.sub || payload.id || 'anonymous'}`;
      } catch (e) {
        // If token parsing fails, use anonymous
      }
    }
    
    // Check if we have a session ID in localStorage
    let sessionId = localStorage.getItem('content_session_id');
    if (!sessionId) {
      sessionId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('content_session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Identifies which content to display based on the user's chat message
   * @param message - The chat message from the user
   * @param availableContents - Optional list of available content IDs
   * @param candidateContext - Optional candidate context information
   * @returns Promise with content identification result
   */
  static async identifyContent(
    message: string,
    availableContents?: string[],
    candidateContext?: any
  ): Promise<ContentIdentificationResult> {
    try {
      console.log('[ContentIdentificationService] Identifying content for message:', message);

      // Build the payload matching your Langflow structure
      const requestPayload: ContentIdentificationRequest = {
        output_type: 'chat',
        input_type: 'chat',
        tweaks: {
          [CONTENT_API_CONFIG.CHAT_INPUT_KEY]: {
            input_value: message,
            // ...(availableContents && { available_contents: JSON.stringify(availableContents) }),
            ...(candidateContext && { candidate_context: JSON.stringify(candidateContext) }),
          },
        },
        session_id: this.getSessionId(),
      };

      console.log('[ContentIdentificationService] Request payload:', requestPayload);

      const response = await fetch(`${CONTENT_API_CONFIG.BASE_URL}${CONTENT_API_CONFIG.ENDPOINT}`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          // If we can't parse the error response, use the default message
        }

        throw new ContentIdentificationApiError(
          `Content Identification API request failed: ${errorMessage}`,
          response.status,
          response
        );
      }

      const apiResponse: ContentIdentificationApiResponse = await response.json();

      // Parse the response according to the specified hierarchy
      const textData = this.extractTextFromResponse(apiResponse);
      
      // Parse the JSON string in the text field
      const identificationResult = this.parseIdentificationResult(textData);

      console.log('[ContentIdentificationService] Content identified:', {
        contentId: identificationResult,
        message: message,
      });

      return identificationResult;

    } catch (error) {
      console.error('[ContentIdentificationService] Error:', error);

      if (error instanceof ContentIdentificationApiError) {
        throw error;
      }

      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ContentIdentificationApiError(
          'Unable to connect to content identification service. Please check if the service is running.',
          0
        );
      }

      throw new ContentIdentificationApiError(
        `Unexpected error during content identification: ${error instanceof Error ? error.message : 'Unknown error'}`,
        500
      );
    }
  }

  /**
   * Extracts the text field from the API response hierarchy
   * @param response - The API response object
   * @returns The text content containing the identification result
   */
  private static extractTextFromResponse(response: ContentIdentificationApiResponse): string {
    try {
      // Navigate the hierarchy: outputs[0].outputs[0].results.message.text
      const text = response?.outputs?.[0]?.outputs?.[0]?.results?.message?.text;
      
      if (!text) {
        throw new Error('Text field not found in the expected response hierarchy');
      }

      return text;
    } catch (error) {
      console.error('[ContentIdentificationService] Error extracting text from response:', error);
      throw new ContentIdentificationApiError(
        'Invalid response format: Unable to extract content identification from API response',
        500,
        response
      );
    }
  }

  /**
   * Parses the text response to extract the content_id
   * @param textData - The text data containing the content_id
   * @returns Content ID string
   */
  private static parseIdentificationResult(textData: string): ContentIdentificationResult {
    try {
      // Clean the text data by removing markdown code block formatting if present
      let cleanedTextData = textData.trim();
      
      // Check if the text is wrapped in markdown code blocks and extract the content
      if (cleanedTextData.startsWith('```json')) {
        // Find the end of the opening markdown tag
        const startIndex = cleanedTextData.indexOf('\n', 7); // 7 is length of '```json'
        if (startIndex !== -1) {
          // Find the closing markdown tag
          const endIndex = cleanedTextData.lastIndexOf('```');
          if (endIndex !== -1 && endIndex > startIndex) {
            cleanedTextData = cleanedTextData.substring(startIndex + 1, endIndex).trim();
          }
        }
      } else if (cleanedTextData.startsWith('```')) {
        // Handle generic code blocks
        const startIndex = cleanedTextData.indexOf('\n');
        if (startIndex !== -1) {
          const endIndex = cleanedTextData.lastIndexOf('```');
          if (endIndex !== -1 && endIndex > startIndex) {
            cleanedTextData = cleanedTextData.substring(startIndex + 1, endIndex).trim();
          }
        }
      }
      
      // Remove any quotes if present
      cleanedTextData = cleanedTextData.replace(/^["']|["']$/g, '');
      
      // Validate that we have a non-empty content_id
      if (!cleanedTextData || cleanedTextData.length === 0) {
        throw new Error('Empty content_id returned from API');
      }
  
      console.log('[ContentIdentificationService] Parsed content_id:', cleanedTextData);
      
      return cleanedTextData;
  
    } catch (error) {
      console.error('[ContentIdentificationService] Error parsing identification result:', error);
      console.error('[ContentIdentificationService] Raw text data:', textData);
      
      throw new ContentIdentificationApiError(
        `Failed to parse content identification result: ${error instanceof Error ? error.message : 'Invalid format'}`,
        500,
        { rawText: textData }
      );
    }
  }


  /**
   * Test the content identification service connection
   * @returns Promise indicating if the service is reachable
   */
  static async testConnection(): Promise<boolean> {
    try {
      // Test with a simple message
      const result = await this.identifyContent('test');
      return !!result;
    } catch (error) {
      console.warn('[ContentIdentificationService] Connection test failed:', error);
      return false;
    }
  }

  /**
   * Get the current API configuration
   */
  static getConfig() {
    return {
      baseUrl: CONTENT_API_CONFIG.BASE_URL,
      endpoint: CONTENT_API_CONFIG.ENDPOINT,
      chatInputKey: CONTENT_API_CONFIG.CHAT_INPUT_KEY,
      fullUrl: `${CONTENT_API_CONFIG.BASE_URL}${CONTENT_API_CONFIG.ENDPOINT}`,
    };
  }
}

export default ContentIdentificationService;

